import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { Stepper, Step } from '@/components/wizard/Stepper'
import { Step1BasicInfo } from './wizard-steps/Step1BasicInfo'
import { Step2ParseConfig, ParseConfig, DEFAULT_PARSE_CONFIG } from './wizard-steps/Step2ParseConfig'
import { Step3RetrievalEnhancement, RetrievalConfig, DEFAULT_RETRIEVAL_CONFIG } from './wizard-steps/Step3RetrievalEnhancement'
import { Step4Advanced } from './wizard-steps/Step4Advanced'
import { Step5Review } from './wizard-steps/Step5Review'
import knowledgeApi from '@/lib/knowledge-api'
import { useQueryClient } from '@tanstack/react-query'
import { useLinkTagsetToKB } from '@/hooks/useTagsets'

const STEPS: Step[] = [
  { id: 1, name: 'Basic Info' },
  { id: 2, name: 'Parse Config' },
  { id: 3, name: 'Retrieval' },
  { id: 4, name: 'Advanced' },
  { id: 5, name: 'Review' },
]

interface WizardData {
  name: string
  description: string
  parseConfig: ParseConfig
  retrievalConfig: RetrievalConfig
  selectedTagsetIds: string[]
}

interface ValidationErrors {
  name?: string
}

const initialWizardData: WizardData = {
  name: '',
  description: '',
  parseConfig: DEFAULT_PARSE_CONFIG,
  retrievalConfig: DEFAULT_RETRIEVAL_CONFIG,
  selectedTagsetIds: [],
}

export function CreateKBWizard() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const linkTagset = useLinkTagsetToKB()

  const [currentStep, setCurrentStep] = useState(1)
  const [wizardData, setWizardData] = useState<WizardData>(initialWizardData)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)

  const validateStep = (step: number): boolean => {
    const newErrors: ValidationErrors = {}

    switch (step) {
      case 1:
        if (!wizardData.name.trim()) {
          newErrors.name = 'Name is required'
        }
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 5))
    }
  }

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }

  const handleStepClick = (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step)
    }
  }

  const handleCancel = () => {
    const hasData = wizardData.name.trim() || wizardData.description.trim()

    if (hasData) {
      setShowCancelDialog(true)
    } else {
      navigate('/app/knowledge')
    }
  }

  const handleConfirmCancel = () => {
    setShowCancelDialog(false)
    navigate('/app/knowledge')
  }

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return

    setIsSubmitting(true)

    try {
      const payload = {
        name: wizardData.name.trim(),
        description: wizardData.description.trim(),
        chunk_method: wizardData.parseConfig.chunkMethod,
        pagerank: wizardData.retrievalConfig.pagerank,
        parser_config: {
          chunk_token_num: wizardData.parseConfig.chunkTokenNum,
          delimiter: wizardData.parseConfig.delimiter,
          layout_recognize: wizardData.parseConfig.layoutRecognize,
          auto_keywords: wizardData.retrievalConfig.autoKeywords,
          auto_questions: wizardData.retrievalConfig.autoQuestions,
        },
      }

      const response = await knowledgeApi.post('/knowledge/api/kb/create/', payload)

      if (response.data.success) {
        const newKbId = response.data.knowledge_base.id

        // Link selected tagsets (fire-and-forget, don't block navigation)
        if (wizardData.selectedTagsetIds.length > 0) {
          for (const tagsetId of wizardData.selectedTagsetIds) {
            try {
              await linkTagset.mutateAsync({ tagsetId, kbId: newKbId })
            } catch {
              // Non-critical — user can link manually later
            }
          }
        }

        toast({
          title: 'Knowledge base created',
          description: `"${wizardData.name}" has been created successfully.`,
        })
        queryClient.invalidateQueries({ queryKey: ['knowledge-bases'] })
        navigate(`/app/knowledge/${newKbId}`)
      } else {
        throw new Error(response.data.error || 'Failed to create knowledge base')
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.message || 'Failed to create knowledge base'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1BasicInfo
            name={wizardData.name}
            description={wizardData.description}
            onNameChange={(name) => {
              setWizardData((prev) => ({ ...prev, name }))
              if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }))
            }}
            onDescriptionChange={(description) =>
              setWizardData((prev) => ({ ...prev, description }))
            }
            errors={errors}
          />
        )
      case 2:
        return (
          <Step2ParseConfig
            config={wizardData.parseConfig}
            onConfigChange={(parseConfig) =>
              setWizardData((prev) => ({ ...prev, parseConfig }))
            }
          />
        )
      case 3:
        return (
          <Step3RetrievalEnhancement
            config={wizardData.retrievalConfig}
            onConfigChange={(retrievalConfig) =>
              setWizardData((prev) => ({ ...prev, retrievalConfig }))
            }
          />
        )
      case 4:
        return (
          <Step4Advanced
            selectedTagsetIds={wizardData.selectedTagsetIds}
            onTagsetSelectionChange={(selectedTagsetIds) =>
              setWizardData((prev) => ({ ...prev, selectedTagsetIds }))
            }
          />
        )
      case 5:
        return (
          <Step5Review
            name={wizardData.name}
            description={wizardData.description}
            parseConfig={wizardData.parseConfig}
            retrievalConfig={wizardData.retrievalConfig}
          />
        )
      default:
        return null
    }
  }

  const stepName = STEPS.find((s) => s.id === currentStep)?.name || ''

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Create Knowledge Base</h1>
        <p className="text-muted-foreground mt-1">
          Step {currentStep} of 5: {stepName}
        </p>
      </div>

      {/* Stepper */}
      <div className="mb-8">
        <Stepper
          steps={STEPS}
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />
      </div>

      {/* Step Content */}
      <div className="min-h-[400px] mb-8">{renderStepContent()}</div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between border-t pt-6">
        <Button variant="ghost" onClick={handleCancel}>
          Cancel
        </Button>

        <div className="flex gap-3">
          {currentStep > 1 && (
            <Button variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}

          {currentStep < 5 ? (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Knowledge Base'
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your progress will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No, continue editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel}>
              Yes, discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
